import {
  customElement,
  DeesElement,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';

@customElement('dotglobal-agent-view')
export class DotglobalAgentView extends DeesElement {
  public static styles = [
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        color: var(--text-primary, #0a0a0a);
        font-family: var(--font-sans);
      }

      * {
        box-sizing: border-box;
      }

      button {
        border: 0;
        background: none;
        color: inherit;
        font: inherit;
        cursor: pointer;
      }

      dees-icon {
        display: inline-flex;
        flex-shrink: 0;
      }

      .page {
        width: 100%;
        max-width: var(--maxw, 1180px);
        margin: 0 auto;
        padding: 0 24px;
      }

      .content {
        padding-top: 24px;
        padding-bottom: 56px;
      }

      .stack {
        display: flex;
        flex-direction: column;
      }

      .row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
      }

      .grow {
        flex: 1;
      }

      .badge {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 2px 8px;
        border-radius: 999px;
        color: #8b5cf6;
        background: color-mix(in srgb, #8b5cf6 14%, transparent);
        font-size: 11.5px;
        font-weight: 500;
        line-height: 1.5;
      }

      .btn {
        height: 40px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        padding: 0 18px;
        border: 1px solid var(--border-strong, #cccccc);
        border-radius: 6px;
        color: var(--text-primary, #0a0a0a);
        background: var(--bg-tertiary, #f4f4f5);
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
      }

      .btn.primary {
        color: var(--accent-fg, #ffffff);
        border-color: var(--accent, #3b82f6);
        background: var(--accent, #3b82f6);
      }

      .muted {
        color: var(--text-muted, #71717a);
      }

      .sec {
        color: var(--text-secondary, #333333);
      }

      .hero-shell {
        border-bottom: 1px solid var(--border-default, #e4e4e7);
        background: var(--bg-secondary, #fafafa);
      }

      .hero {
        padding-top: 48px;
        padding-bottom: 40px;
      }

      h1 {
        max-width: 720px;
        margin: 16px 0 12px;
        font-family: var(--font-display);
        font-size: 38px;
        font-weight: 700;
        line-height: 1.08;
        letter-spacing: -0.03em;
      }

      .lead {
        max-width: 600px;
        margin: 0;
        font-size: 16px;
        line-height: 1.55;
      }

      .layout {
        display: flex;
        align-items: flex-start;
        gap: 24px;
        flex-wrap: wrap;
      }

      .steps {
        min-width: 300px;
        flex: 1;
      }

      .termwrap {
        width: 440px;
        max-width: 100%;
        flex-shrink: 0;
      }

      .agent-glyph {
        position: relative;
        width: 40px;
        height: 40px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        border-radius: 6px;
        color: #8b5cf6;
        background: color-mix(in srgb, #8b5cf6 18%, transparent);
      }

      .step-n {
        position: absolute;
        top: -6px;
        left: -6px;
        width: 18px;
        height: 18px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        color: #ffffff;
        background: var(--accent, #3b82f6);
        font-size: 10px;
        font-weight: 700;
      }

      .term {
        overflow: hidden;
        border: 1px solid var(--border-strong, #cccccc);
        border-radius: 6px;
        background: #0a0a0a;
        color: #d4d4d4;
        font-family: var(--font-mono);
        font-size: 12.5px;
        line-height: 1.7;
      }

      .term-h {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 12px;
        border-bottom: 1px solid var(--border-strong, #cccccc);
      }

      .term-dot {
        width: 11px;
        height: 11px;
        border-radius: 50%;
      }

      .term-b {
        padding: 12px 14px;
        white-space: pre-wrap;
      }

      .divider {
        height: 1px;
        background: var(--border-default, #e4e4e7);
      }

      .proofgrid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 16px;
      }

      .card {
        padding: 18px;
        border: 1px solid var(--border-default, #e4e4e7);
        border-radius: 8px;
        background: var(--bg-primary, #ffffff);
      }

      @media (max-width: 820px) {
        .page {
          padding: 0 16px;
        }

        h1 {
          font-size: 32px;
        }
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="stack" style="flex: 1">
        <div class="hero-shell">
          <div class="page hero">
            <span class="badge">${this.icon('Sparkles')} open source · foss propose v0.4</span>
            <h1>Contribute without a fork. Let an agent do the legwork.</h1>
            <p class="sec lead">Proposed changes don't arrive as pull requests on foss.global. They arrive as <b>proposal issues</b> - a clean diff an agent investigated and verified on your own machine.</p>
            <div class="row" style="gap: 10px; margin-top: 24px">
              <button class="btn primary" @click=${() => this.emitCopy('pnpm add -g @foss.global/propose')}>${this.icon('Download')} Install the agent</button>
              <button class="btn" @click=${() => this.emitNavigate('#/repo/serve.zone/dcrouter/issues')}>${this.icon('Diff')} See an example proposal</button>
            </div>
          </div>
        </div>
        <div class="page content stack" style="gap: 32px">
          <div class="layout">
            <div class="steps stack" style="gap: 14px">
              ${this.renderStep(1, 'Terminal', 'Run it locally', 'Install the open-source agent and run foss propose in any repo you cloned. It works against your real working tree - your unpushed edits, your environment.')}
              ${this.renderStep(2, 'Search', 'It investigates', 'The agent reproduces the issue, runs the test suite, and isolates a minimal change. Nothing leaves your machine until you approve.')}
              ${this.renderStep(3, 'Diff', 'It drafts a patch', 'You get a clean diff plus a written rationale and the test results that back it up - reviewed by you before anything is sent.')}
              ${this.renderStep(4, 'CircleDot', 'It opens an issue', 'The patch is filed as a proposal issue on foss.global. No fork, no pull request - maintainers review the diff inline and apply it upstream.')}
            </div>
            <div class="termwrap">${this.renderTermDemo()}</div>
          </div>
          <div class="divider"></div>
          <div class="proofgrid">
            ${this.renderProofCard('Shield', 'Local-first & private', 'The agent reads your working tree on your machine. Code only leaves when you approve the patch.')}
            ${this.renderProofCard('Scale', 'No fork sprawl', 'No thousand stale forks. A proposal is one issue with one diff, reviewed in place.')}
            ${this.renderProofCard('Zap', 'Verified before filing', 'Every proposal ships with reproduction steps and a green test run attached.')}
          </div>
        </div>
      </div>
    `;
  }

  private renderStep(indexArg: number, iconArg: string, titleArg: string, descriptionArg: string): TemplateResult {
    return html`
      <div class="row" style="align-items: flex-start; gap: 14px">
        <div class="agent-glyph">${this.icon(iconArg)}<span class="step-n">${indexArg}</span></div>
        <div style="flex: 1">
          <div style="font-weight: 600; font-size: 14.5px">${titleArg}</div>
          <div class="sec" style="font-size: 13px; line-height: 1.55; margin-top: 3px">${descriptionArg}</div>
        </div>
      </div>
    `;
  }

  private renderProofCard(iconArg: string, titleArg: string, descriptionArg: string): TemplateResult {
    return html`
      <div class="card">
        <span style="color: var(--accent, #3b82f6)">${this.icon(iconArg)}</span>
        <div style="font-weight: 600; font-size: 14px; margin: 12px 0 6px">${titleArg}</div>
        <div class="sec" style="font-size: 13px; line-height: 1.55">${descriptionArg}</div>
      </div>
    `;
  }

  private renderTermDemo(): TemplateResult {
    return html`
      <div class="term">
        <div class="term-h">
          <span class="term-dot" style="background: #ef4444"></span>
          <span class="term-dot" style="background: #f59e0b"></span>
          <span class="term-dot" style="background: #22c55e"></span>
          <span style="margin-left: 6px; font-size: 11px; color: #888">~/code/router</span>
        </div>
        <div class="term-b"><span style="color: #22c55e">$ </span><span style="color: #fafafa">foss propose "describe the issue"</span>
<span style="color: #6b7280"># reading working tree</span>
<span style="color: #6b7280"># reproducing... </span><span style="color: #22c55e">reproduced locally</span>
<span style="color: #6b7280"># running test suite... </span><span style="color: #22c55e">passing with fix</span>
<span style="color: #6b7280"># drafting patch... </span><span style="color: #3b82f6">changed files ready</span>

<span style="color: #fafafa">Open as proposal on foss.global? </span><span style="color: #22c55e">yes</span>
<span style="color: #22c55e">✓ </span><span style="color: #fafafa">Filed proposal issue - patch attached</span></div>
      </div>
    `;
  }

  private emitNavigate(hashArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-navigate', {
      detail: { hash: hashArg },
      bubbles: true,
      composed: true,
    }));
  }

  private emitCopy(textArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-copy', {
      detail: { text: textArg },
      bubbles: true,
      composed: true,
    }));
  }

  private icon(nameArg: string): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${16}></dees-icon>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-agent-view': DotglobalAgentView;
  }
}
